import React, { useState, useMemo } from "react"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Tooltip,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material"
import { PieChart } from "@mui/x-charts/PieChart"
import { BarChart } from "@mui/x-charts/BarChart"
import {
  HowToVote,
  ReportProblem,
  TableChart,
  TravelExplore,
  Groups,
  Leaderboard,
  WarningAmber,
  Download as DownloadIcon,
  Print as PrintIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import dayjs from "dayjs"

import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomAggregationSegregationComponent from "../../components/CustomAggregationSegregationComponent"
import { useQuery, api, showWarning } from "../../api"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  paddingBottom: "40px",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" }, error: { main: "#d32f2f" } },
})

const KPICard = ({ title, value, icon, color, subtitle }) => (
  <Card elevation={2} sx={{ height: "100%", borderLeft: `6px solid ${color}`, borderRadius: 2 }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="body2"
            color="textSecondary"
            fontWeight="bold"
            sx={{ textTransform: "uppercase", mb: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block", fontWeight: "500" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: `${color}1A`, p: 1.5, borderRadius: "50%", display: "flex" }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
)

const EncuestaMantaIndex = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [filtroParroquia, setFiltroParroquia] = useState("TODAS")

  // =========================================================================
  // CONSULTA AL BACKEND CON EXTRACCIÓN DEFENSIVA EXTREMA
  // =========================================================================
  const {
    data: reportes,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["getResultadosEncuestaManta", filtroParroquia],
    queryFn: async () => {
      const payload = {
        encuesta: "ENA2600000101",
        parroquia: filtroParroquia === "TODAS" ? null : filtroParroquia,
      }
      try {
        const res = await api.post("/EjecEncuestas/getAnaliticaManta", payload)

        // Desempaquetado dinámico para evadir la doble/triple anidación
        let rawData = res?.data || {}
        if (rawData.data) rawData = rawData.data
        if (rawData.data) rawData = rawData.data

        return {
          kpi1: Array.isArray(rawData?.kpi1) ? rawData.kpi1 : [],
          kpi2: Array.isArray(rawData?.kpi2) ? rawData.kpi2 : [],
          kpi3: Array.isArray(rawData?.kpi3) ? rawData.kpi3 : [],
        }
      } catch (error) {
        console.error("Error consultando la analítica:", error)
        return { kpi1: [], kpi2: [], kpi3: [] }
      }
    },
    refetchOnWindowFocus: false,
  })

  const data = reportes || { kpi1: [], kpi2: [], kpi3: [] }

  const addId = (rows) => {
    if (!Array.isArray(rows)) return []
    return rows.map((r, i) => ({ ...r, id: r.id || i }))
  }

  // =========================================================================
  // EXPORTACIÓN A PDF (Generación de Reporte Tabular Limpio)
  // =========================================================================
  const handleExportarPDF = () => {
    let exportData = []
    let cols = []
    let title = ""

    if (activeTab === 0) {
      exportData = data.kpi1
      cols = [
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Candidato a Alcalde", key: "Intencion_Alcalde" },
        { header: "Votos", key: "Cantidad_Votos" },
        { header: "% Segmento", key: "Pct_Segmento" },
      ]
      title = "Detalle Intención de Voto"
    } else if (activeTab === 1) {
      exportData = data.kpi2
      cols = [
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Problema Principal", key: "Problema_Principal" },
        { header: "Menciones", key: "Menciones" },
        { header: "Score", key: "Score_Criticidad" },
      ]
      title = "Detalle Dolores Ciudadanos"
    } else if (activeTab === 2) {
      exportData = data.kpi3
      cols = [
        { header: "Nº", key: "No_Pregunta" },
        { header: "Pregunta", key: "Pregunta" },
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Respuesta Obtenida", key: "Respuesta" },
        { header: "Frecuencia", key: "Total_Respuestas" },
        { header: "% Relativo", key: "Pct_Relativo_Segmento" },
      ]
      title = "Matriz Global Transversal (Cubo)"
    } else {
      return showWarning("No hay tabla disponible en esta vista para imprimir.")
    }

    if (!exportData || exportData.length === 0) {
      return showWarning("No hay datos tabulados para generar el PDF.")
    }

    // Creación de ventana de impresión limpia
    const printWindow = window.open("", "_blank")
    const htmlContent = `
      <html>
        <head>
          <title>Reporte - ${title}</title>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 20px; color: #333; }
            h2 { text-align: center; color: #196C87; margin-bottom: 5px; }
            h4 { text-align: center; color: #666; margin-top: 0; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f7fa; color: #196C87; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 30px; font-size: 10px; color: #999; text-align: right; }
          </style>
        </head>
        <body>
          <h2>Estudio de Valoración Social - Manta</h2>
          <h4>${title} | Filtro: ${filtroParroquia}</h4>
          <table>
            <thead>
              <tr>${cols.map((c) => `<th>${c.header}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${exportData.map((row) => `<tr>${cols.map((c) => `<td>${row[c.key] !== undefined && row[c.key] !== null ? row[c.key] : ""}</td>`).join("")}</tr>`).join("")}
            </tbody>
          </table>
          <div class="footer">Generado por SIAC Web Analytics - ${dayjs().format("DD/MM/YYYY HH:mm")}</div>
          <script>
            window.onload = function() { 
              window.print(); 
              setTimeout(function(){ window.close(); }, 500); 
            }
          </script>
        </body>
      </html>
    `
    printWindow.document.write(htmlContent)
    printWindow.document.close()
  }

  // =========================================================================
  // FUNCIÓN DE EXPORTACIÓN DINÁMICA A CSV
  // =========================================================================
  const handleExportarCSV = () => {
    let exportData = []
    let cols = []
    let filename = ""

    if (activeTab === 0) {
      exportData = data.kpi1
      cols = [
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Candidato a Alcalde", key: "Intencion_Alcalde" },
        { header: "Votos (Frecuencia)", key: "Cantidad_Votos" },
        { header: "% en Segmento", key: "Pct_Segmento" },
      ]
      filename = `Intencion_Voto_Manta_${dayjs().format("YYYYMMDD_HHmm")}.csv`
    } else if (activeTab === 1) {
      exportData = data.kpi2
      cols = [
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Problema Principal", key: "Problema_Principal" },
        { header: "Menciones", key: "Menciones" },
        { header: "Score Criticidad", key: "Score_Criticidad" },
      ]
      filename = `Dolores_Ciudadanos_Manta_${dayjs().format("YYYYMMDD_HHmm")}.csv`
    } else if (activeTab === 2) {
      exportData = data.kpi3
      cols = [
        { header: "Nº Pregunta", key: "No_Pregunta" },
        { header: "Pregunta", key: "Pregunta" },
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Respuesta Obtenida", key: "Respuesta" },
        { header: "Frecuencia", key: "Total_Respuestas" },
        { header: "% Relativo", key: "Pct_Relativo_Segmento" },
      ]
      filename = `Matriz_Global_Manta_${dayjs().format("YYYYMMDD_HHmm")}.csv`
    } else {
      return showWarning("No hay datos en esta vista para exportar.")
    }

    if (!exportData || exportData.length === 0) {
      return showWarning("No hay datos en esta vista para exportar.")
    }

    const headerRow = cols.map((c) => `"${c.header}"`).join(",")
    const rows = exportData.map((row) => {
      return cols.map((c) => `"${row[c.key] !== undefined && row[c.key] !== null ? row[c.key] : ""}"`).join(",")
    })

    const csvContent = "\uFEFF" + [headerRow, ...rows].join("\n")
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // =========================================================================
  // PROCESAMIENTO DE DATOS PARA GRÁFICOS Y TARJETAS KPI
  // =========================================================================
  const metricasGenerales = useMemo(() => {
    let totalEncuestas = 0
    const intencionMap = {}
    const problemasMap = {}

    if (data.kpi1 && data.kpi1.length > 0) {
      data.kpi1.forEach((row) => {
        totalEncuestas += Number(row.Cantidad_Votos || 0)
        intencionMap[row.Intencion_Alcalde] =
          (intencionMap[row.Intencion_Alcalde] || 0) + Number(row.Cantidad_Votos || 0)
      })
    }

    if (data.kpi2 && data.kpi2.length > 0) {
      data.kpi2.forEach((row) => {
        problemasMap[row.Problema_Principal] =
          (problemasMap[row.Problema_Principal] || 0) + Number(row.Score_Criticidad || 0)
      })
    }

    const intencionArr = Object.entries(intencionMap)
      .map(([k, v]) => ({ label: k, value: v }))
      .sort((a, b) => b.value - a.value)
    const problemasArr = Object.entries(problemasMap)
      .map(([k, v]) => ({ label: k, value: v }))
      .sort((a, b) => b.value - a.value)

    const liderIntencion = intencionArr.length > 0 ? intencionArr[0].label : "N/A"
    const topProblema = problemasArr.length > 0 ? problemasArr[0].label : "N/A"

    return { totalEncuestas, intencionArr, problemasArr, liderIntencion, topProblema }
  }, [data])

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1, color: "#196C87" }}>
          <TravelExplore sx={{ fontSize: 36, mr: 1 }} />
          <Typography variant="h4" fontWeight="bold">
            Estudio de Valoración Social - Manta
          </Typography>
        </Box>
        <Typography variant="body1" align="center" color="textSecondary" mb={3}>
          Dashboard Analítico y Cuadros de Mando Transaccionales
        </Typography>

        <CustomBackdrop isLoading={isLoading} />

        <StyledRoot>
          {/* FILTROS GLOBALES */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, borderTop: "4px solid #196C87" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Filtrar por Parroquia"
                  value={filtroParroquia}
                  onChange={(e) => setFiltroParroquia(e.target.value)}
                  size="small"
                >
                  <MenuItem value="TODAS">Consolidado General (Todas)</MenuItem>
                  <MenuItem value="Tarqui">Tarqui</MenuItem>
                  <MenuItem value="Manta">Manta</MenuItem>
                  <MenuItem value="Los Esteros">Los Esteros</MenuItem>
                  <MenuItem value="Eloy Alfaro">Eloy Alfaro</MenuItem>
                  <MenuItem value="UrbiManta">UrbiManta</MenuItem>
                  <MenuItem value="San Mateo">San Mateo</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} alignItems="center">
                  {isFetching && !isLoading && <CircularProgress size={16} sx={{ mr: 1.5, color: "#196C87" }} />}
                  <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
                    Mostrando resultados demográficos consolidados de la campaña.
                  </Typography>
                  <Tooltip title="Recargar datos ahora">
                    <span>
                      <IconButton
                        onClick={() => refetch()}
                        color="primary"
                        disabled={isFetching || isLoading}
                        size="small"
                        sx={{ border: "1px solid #196C87", bgcolor: "white" }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* TARJETAS KPI DE RESUMEN EJECUTIVO */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Muestra Tabulada"
                value={metricasGenerales.totalEncuestas}
                icon={<Groups />}
                color="#196C87"
                subtitle={`Filtro aplicado: ${filtroParroquia}`}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Candidato Líder"
                value={metricasGenerales.liderIntencion}
                icon={<Leaderboard />}
                color="#2E7D32"
                subtitle="Mayor intención de voto en la zona"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Principal Demanda"
                value={metricasGenerales.topProblema}
                icon={<WarningAmber />}
                color="#d32f2f"
                subtitle="Problema con mayor score de criticidad"
              />
            </Grid>
          </Grid>

          {/* GRÁFICOS ANALÍTICOS */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={5}>
              <Paper
                elevation={2}
                sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Intención de Voto (Consolidado)
                  </Typography>
                </Box>
                <Box
                  sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 350 }}
                >
                  {metricasGenerales.intencionArr.length > 0 ? (
                    <PieChart
                      series={[
                        {
                          data: metricasGenerales.intencionArr.map((d, i) => ({
                            id: i,
                            label: d.label,
                            value: d.value,
                          })),
                          innerRadius: 60,
                          paddingAngle: 2,
                          cornerRadius: 4,
                        },
                      ]}
                      height={300}
                      slotProps={{
                        legend: { direction: "column", position: { vertical: "middle", horizontal: "right" } },
                      }}
                    />
                  ) : (
                    <Typography color="textSecondary" fontStyle="italic">
                      Sin datos tabulados.
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper
                elevation={2}
                sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                  <Typography variant="h6" color="error" fontWeight="bold">
                    Score de Dolores Ciudadanos
                  </Typography>
                </Box>
                <Box sx={{ flexGrow: 1, minHeight: 350 }}>
                  {metricasGenerales.problemasArr.length > 0 ? (
                    <BarChart
                      dataset={metricasGenerales.problemasArr.slice(0, 8)}
                      yAxis={[{ scaleType: "band", dataKey: "label" }]}
                      series={[{ dataKey: "value", color: "#ed6c02" }]}
                      layout="horizontal"
                      height={350}
                      margin={{ left: 150, right: 30, top: 20, bottom: 20 }}
                    />
                  ) : (
                    <Typography color="textSecondary" fontStyle="italic">
                      Sin datos tabulados.
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* TABS DE NAVEGACIÓN */}
          <Paper elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab icon={<HowToVote />} label="DETALLE INTENCIÓN DE VOTO" sx={{ fontWeight: "bold" }} />
              <Tab icon={<ReportProblem />} label="DETALLE DOLORES CIUDADANOS" sx={{ fontWeight: "bold" }} />
              <Tab icon={<TableChart />} label="MATRIZ GLOBAL (CUBO)" sx={{ fontWeight: "bold" }} />
              <Tab icon={<MapIcon />} label="MAPA DE CALOR (GEO-ESTRATÉGICO)" sx={{ fontWeight: "bold" }} />
            </Tabs>
          </Paper>

          {/* CONTENEDOR DE TABLAS Y MAPA */}
          <Paper elevation={3} sx={{ width: "100%", borderRadius: 2, p: 2, minHeight: 400 }}>
            {/* Solo mostramos la barra de exportación en los tabs de tablas (0, 1, 2) */}
            {activeTab !== 3 && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                  Listado Detallado (Tabla de Contingencia)
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handleExportarPDF}
                    sx={{ fontWeight: "bold", bgcolor: "white", mr: 2 }}
                    disabled={
                      (activeTab === 0 && data.kpi1.length === 0) ||
                      (activeTab === 1 && data.kpi2.length === 0) ||
                      (activeTab === 2 && data.kpi3.length === 0)
                    }
                  >
                    Guardar / Imprimir PDF
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportarCSV}
                    sx={{ fontWeight: "bold" }}
                    disabled={
                      (activeTab === 0 && data.kpi1.length === 0) ||
                      (activeTab === 1 && data.kpi2.length === 0) ||
                      (activeTab === 2 && data.kpi3.length === 0)
                    }
                  >
                    Exportar a CSV
                  </Button>
                </Box>
              </Stack>
            )}

            {/* TABLA 1 */}
            {activeTab === 0 && data.kpi1.length > 0 && (
              <CustomAggregationSegregationComponent
                data={addId(data.kpi1)}
                columnsConfig={() => [
                  { accessorKey: "Parroquia", header: "Parroquia", size: 180 },
                  { accessorKey: "Sexo", header: "Sexo", size: 140 },
                  { accessorKey: "Rango_Edad", header: "Rango Edad", size: 150 },
                  { accessorKey: "Intencion_Alcalde", header: "Candidato a Alcalde", size: 300 },
                  { accessorKey: "Cantidad_Votos", header: "Votos (Frecuencia)", size: 180 },
                  {
                    accessorKey: "Pct_Segmento",
                    header: "% en Segmento",
                    size: 180,
                    Cell: ({ cell }) => `${cell.getValue()}%`,
                  },
                ]}
                tableOptions={{
                  initialState: { sorting: [{ id: "Cantidad_Votos", desc: true }] },
                }}
              />
            )}

            {/* TABLA 2 */}
            {activeTab === 1 && data.kpi2.length > 0 && (
              <CustomAggregationSegregationComponent
                data={addId(data.kpi2)}
                columnsConfig={() => [
                  { accessorKey: "Parroquia", header: "Parroquia", size: 180 },
                  { accessorKey: "Sexo", header: "Sexo", size: 140 },
                  { accessorKey: "Rango_Edad", header: "Rango Edad", size: 150 },
                  { accessorKey: "Problema_Principal", header: "Problema Principal", size: 350 },
                  { accessorKey: "Menciones", header: "Menciones", size: 150 },
                  { accessorKey: "Score_Criticidad", header: "Score (Criticidad)", size: 180 },
                ]}
                tableOptions={{
                  initialState: { sorting: [{ id: "Score_Criticidad", desc: true }] },
                }}
              />
            )}

            {/* TABLA 3 */}
            {activeTab === 2 && data.kpi3.length > 0 && (
              <CustomAggregationSegregationComponent
                data={addId(data.kpi3)}
                columnsConfig={() => [
                  { accessorKey: "No_Pregunta", header: "Nº", size: 100 },
                  { accessorKey: "Pregunta", header: "Pregunta", size: 450 },
                  { accessorKey: "Parroquia", header: "Parroquia", size: 180 },
                  { accessorKey: "Sexo", header: "Sexo", size: 140 },
                  { accessorKey: "Rango_Edad", header: "Rango Edad", size: 150 },
                  { accessorKey: "Respuesta", header: "Respuesta Obtenida", size: 350 },
                  { accessorKey: "Total_Respuestas", header: "Frecuencia", size: 150 },
                  {
                    accessorKey: "Pct_Relativo_Segmento",
                    header: "% Relativo",
                    size: 160,
                    Cell: ({ cell }) => `${cell.getValue()}%`,
                  },
                ]}
                tableOptions={{
                  initialState: { sorting: [{ id: "No_Pregunta", desc: false }] },
                }}
              />
            )}

            {/* MAPA LOOKER STUDIO (Pestaña 4) */}
            {activeTab === 3 && (
              <Box sx={{ display: "flex", flexDirection: "column", height: 600 }}>
                <Box mb={2}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Mapa de Control Territorial y Geolocalización
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <b>Instrucciones:</b> Para visualizar el mapa de calor interactivo, ingresa a tu cuenta de{" "}
                    <b>Looker Studio</b>, conecta la vista SQL proporcionada en el Backend, genera un informe usando{" "}
                    <b>Google Maps</b> y obtén el enlace de incrustación <i>(Archivo &gt; Insertar informe)</i>. Luego
                    reemplaza el <code>src</code> en el código de este componente.
                  </Typography>
                </Box>
                <iframe
                  title="Mapa Calor Looker Studio"
                  width="100%"
                  height="100%"
                  src="https://lookerstudio.google.com/embed/reporting/TUID/page/TUPAGINA"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: "8px", flexGrow: 1, backgroundColor: "#f5f7fa" }}
                  allowFullScreen
                  sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                ></iframe>
              </Box>
            )}

            {/* MENSAGES DE EMPTY STATES PARA TABLAS */}
            {activeTab !== 3 &&
              ((activeTab === 0 && data.kpi1.length === 0) ||
                (activeTab === 1 && data.kpi2.length === 0) ||
                (activeTab === 2 && data.kpi3.length === 0)) &&
              !isLoading &&
              !isFetching && (
                <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 10 }}>
                  No hay información detallada disponible para esta vista o no ha finalizado la carga.
                </Typography>
              )}
          </Paper>
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default EncuestaMantaIndex
